import graphql__594452ec364a9d6dbf35 from "./__generated__/AppTenantMenuQuery.graphql.ts";import { Button, Heading, icons, Popover } from '@portal/components'
import graphql from 'babel-plugin-relay/macro'
import { useAppToDatabaseRename } from 'components/hooks'
import { TenantContext } from 'components/tenant'
import paths, { isStaticPath } from 'paths'
import { Suspense, useContext, useEffect, useState } from 'react'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { useLocation, useNavigate } from 'react-router-dom'

import { AppTenantMenuQuery } from './__generated__/AppTenantMenuQuery.graphql'
import AppTenantMenuButton from './AppTenantMenuButton'
import OrganizationAppsMenuItem from './OrganizationAppsMenuItem'
import OrganizationsMenuItem from './OrganizationsMenuItem'
import PersonalAppsMenuItem from './PersonalAppsMenuItem'
import RecentOrganizationAppsMenuItem from './RecentOrganizationAppsMenuItem'

const { ChevronUpDown } = icons

const appTenantQuery = graphql__594452ec364a9d6dbf35

export const AppTenantMenuHeading = ({ heading }: { heading: string }) => (
  <Heading
    level={3}
    className="text-sm sm:text-sm mb-0.5 px-2 text-foreground-subtle"
  >
    {heading}
  </Heading>
)

const AppTenantMenuSeparator = () => (
  <div className="h-px w-full bg-border-normal" />
)

export const AppTenantMenuSection = ({
  children,
}: {
  children: React.ReactNode
}) => <div className="py-2.5 px-2">{children}</div>

export default function AppTenantMenu() {
  const { tenant, updateTenant, getTenantHome } = useContext(TenantContext)
  const skipOrganization = !tenant

  const { viewer } = useLazyLoadQuery<AppTenantMenuQuery>(appTenantQuery, {
    slug: tenant || '',
    skipOrganization,
  })

  const [isOpen, setIsOpen] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  const activeTenant = viewer.organization?.name || 'Personal'

  const { databasesText, titlePluralText, isRenamed } = useAppToDatabaseRename()

  const handleSwitchTenant = (tenant: string | null) => {
    const redirectPath = getTenantHome(tenant)

    // NOTE: this step is important, otherwise if we route to /, we will be redirected back
    // here since the tenant wasn't updated.
    updateTenant(tenant)

    const pathname = location.pathname
    const shouldRedirect = !isStaticPath(pathname, isRenamed)
    if (shouldRedirect) {
      navigate(redirectPath)
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} data-testid="appTenantMenu">
      <Popover.Trigger asChild>
        <div className="flex items-center">
          <Button
            data-testid="appTenantMenuTrigger"
            className="group flex max-w-full flex-initial appearance-none border-none hover:!bg-transparent items-center px-2 text-base outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-outline sm:px-4"
          >
            <span className="mr-2 truncate whitespace-nowrap text-foreground-normal text-base">
              {activeTenant}
            </span>

            <ChevronUpDown className="h-4 w-4 flex-none text-foreground-subtle group-hover:text-foreground-normal group-focus-visible:text-foreground-normal" />
          </Button>
        </div>
      </Popover.Trigger>

      <Popover.Content
        className="sm:!min-w-[360px] !p-0 relative"
        align="start"
        role="menu"
      >
        <div className="max-h-[calc(100vh-96px)] w-full overflow-y-auto sm:max-h-[calc(100vh-72px)]">
          <AppTenantMenuSection>
            <AppTenantMenuHeading heading={titlePluralText} />
            {tenant && (
              <Suspense fallback={null}>
                <OrganizationAppsMenuItem isOpen={isOpen} slug={tenant} />
              </Suspense>
            )}

            {!tenant && (
              <Suspense fallback={null}>
                <PersonalAppsMenuItem isOpen={isOpen} />
              </Suspense>
            )}

            <Suspense fallback={null}>
              <RecentOrganizationAppsMenuItem isOpen={isOpen} />
            </Suspense>
          </AppTenantMenuSection>

          <AppTenantMenuSeparator />

          <AppTenantMenuSection>
            <AppTenantMenuHeading heading="Organizations" />
            <Suspense fallback={null}>
              <OrganizationsMenuItem
                isOpen={isOpen}
                onChangeTenant={handleSwitchTenant}
              />
            </Suspense>
          </AppTenantMenuSection>

          <AppTenantMenuSeparator />

          <AppTenantMenuSection>
            <AppTenantMenuButton
              label={`More organizations & ${databasesText}`}
              onClick={() => navigate(paths.userOrganizations({}))}
              icon="ArrowRight"
              iconPosition="right"
              className="flex justify-between"
            />
          </AppTenantMenuSection>

          <AppTenantMenuSeparator />

          <AppTenantMenuSection>
            <AppTenantMenuButton
              label="New organization"
              onClick={() => navigate(paths.createOrganization())}
              icon="Add"
              iconPosition="right"
              className="flex justify-between"
            />
          </AppTenantMenuSection>
        </div>
      </Popover.Content>
    </Popover>
  )
}
